import { supabase } from '../config/supabase';
import { routeOdKey, type OdPair } from './routeOdKey';

export const DIRECTIONS_CACHE_TTL_HOURS = 24;
/** Max uncached OD pairs sent to Google per plan (rate limit). Rest → static fallback. */
export const DIRECTIONS_MAX_GOOGLE_PAIRS_PER_PLAN = 250;

const IN_CHUNK = 80;

export interface RouteTravelCacheRow {
  od_key: string;
  duration_minutes: number;
  distance_meters?: number | null;
}

export function cacheExpiresAt(from: Date = new Date()): string {
  return new Date(from.getTime() + DIRECTIONS_CACHE_TTL_HOURS * 60 * 60 * 1000).toISOString();
}

/** Load non-expired cache hits for OD keys. */
export async function loadRouteTravelCache(
  odKeys: string[]
): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  if (odKeys.length === 0) return out;

  const nowIso = new Date().toISOString();
  const unique = [...new Set(odKeys)];

  for (let i = 0; i < unique.length; i += IN_CHUNK) {
    const chunk = unique.slice(i, i + IN_CHUNK);
    const { data, error } = await supabase
      .from('route_travel_cache')
      .select('od_key, duration_minutes')
      .in('od_key', chunk)
      .gt('expires_at', nowIso);

    if (error) {
      // Cache miss path — caller still fetches Google / falls back to static
      console.warn('[route_travel_cache] load failed:', error.message);
      continue;
    }
    for (const row of data ?? []) {
      const key = row.od_key as string;
      const mins = row.duration_minutes as number;
      if (typeof key === 'string' && typeof mins === 'number' && Number.isFinite(mins)) {
        out[key] = Math.max(0, Math.ceil(mins));
      }
    }
  }
  return out;
}

export async function upsertRouteTravelCache(
  entries: Array<{
    pair: OdPair;
    durationMinutes: number;
    distanceMeters?: number | null;
  }>
): Promise<void> {
  if (entries.length === 0) return;
  const expires_at = cacheExpiresAt();
  const updated_at = new Date().toISOString();

  const rows = entries.map(({ pair, durationMinutes, distanceMeters }) => ({
    od_key: routeOdKey(pair.from, pair.to),
    from_lat: pair.from.lat,
    from_lng: pair.from.lng,
    to_lat: pair.to.lat,
    to_lng: pair.to.lng,
    travel_mode: 'DRIVE',
    duration_minutes: Math.max(0, Math.ceil(durationMinutes)),
    distance_meters: distanceMeters ?? null,
    source: 'google_routes',
    updated_at,
    expires_at,
  }));

  for (let i = 0; i < rows.length; i += IN_CHUNK) {
    const chunk = rows.slice(i, i + IN_CHUNK);
    const { error } = await supabase.from('route_travel_cache').upsert(chunk, {
      onConflict: 'od_key',
    });
    if (error) {
      console.warn('[route_travel_cache] upsert failed:', error.message);
    }
  }
}
